import React, { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { FetchStatus, LanderCookie, System } from "../commons/Constants";
import {
  yahooXmlFetch,
  getYahooRSUri,
  extractPartnerTermsQs,
} from "./yahooFeed";
import { sendYahooEvent, EventType, ErrorCode } from "./yahooEvent";
import { useLanderConfig } from "../commons/ConfigContext";
import { trackImpression } from "../commons/signals";
import { getCookie } from "../commons/HelperFunctions";
import { YahooAds } from "./YahooAds";
import { YahooRS, RSItem } from "./YahooRS";
import { SearchBox } from "./SearchBox";
import { useYahooSingleLandersScript } from "./useYahooSingleLandersScript";
import classes from "./YahooAds.module.scss";
import PropTypes from "prop-types";
import classNames from "classnames";

interface YahooAdContentProps {
  isAdClicked: boolean;
  xmlResult: Document | null;
  keywords: string[];
  maxAds?: number;
  maxRS?: number;
  adIcon?: React.ReactNode;
  headerMessageId?: string;
}

const YahooAdContent: React.FC<YahooAdContentProps> = ({
  isAdClicked,
  xmlResult,
  keywords,
  maxAds,
  maxRS,
  adIcon,
  headerMessageId,
}) => {
  const partnerTermsQs = extractPartnerTermsQs(xmlResult);

  let relatedContent: React.ReactNode;
  if (isAdClicked) {
    relatedContent = (
      <YahooAds xmlResult={ xmlResult } maxAds={ maxAds } adIcon={ adIcon } />
    );
  } else {
    let rsItems: RSItem[];
    if (partnerTermsQs && keywords.length > 0) {
      rsItems = keywords.map((keyword) => ({
        term: keyword,
        qs: partnerTermsQs,
        searchQuery: keyword,
      }));
    } else {
      const resultSet =
        xmlResult
          ?.getElementById("relatedTerms")
          ?.getElementsByTagName("Search") ?? [];
      rsItems = Array.from(resultSet).map((el) => ({
        term: el.innerHTML,
        qs: el.getAttribute("queryParams") || "",
        searchQuery: "",
      }));
    }
    relatedContent = (
      <YahooRS
        items={ rsItems }
        maxRS={ maxRS }
        headerMessageId={ headerMessageId }
      />
    );
  }

  return (
    <>
      {relatedContent}
      <SearchBox xmlResult={ xmlResult } />
    </>
  );
};

YahooAdContent.propTypes = {
  isAdClicked: PropTypes.bool,
  xmlResult: PropTypes.instanceOf(Document),
};

interface YahooContentProps {
  maxAds?: number;
  maxRS?: number;
  adIcon?: React.ReactNode;
  headerMessageId?: string;
}

export const YahooContent: React.FC<YahooContentProps> = ({
  maxAds,
  maxRS,
  adIcon,
  headerMessageId,
}) => {
  const { landerConfig, mergedConfig } = useLanderConfig();

  const [fetchState, setFetchState] = useState<number>(FetchStatus.PENDING);
  const [xmlResult, setXmlResult] = useState<Document | null>(null);

  const isAdClicked = new URLSearchParams(window.location.search).has("qs");

  useYahooSingleLandersScript(xmlResult);

  // Fire VISIT event on mount
  useEffect(() => {
    sendYahooEvent(EventType.VISIT, landerConfig);
    trackImpression({ section: "page", widget: "lander_page" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // some bots don't have cookies, which results in their ip cookie having an unexpected value
    // in that case, getCookie will return an empty string
    if (!getCookie(LanderCookie.CLIENT_IP)) {
      // Set to BLOCKED state so the user sees error message instead of blank page
      setFetchState(FetchStatus.BLOCKED);
      setXmlResult(null);
      return;
    }
    let ignore = false;

    let yahooUrl: string;
    // forward query params from browser url
    if (isAdClicked) yahooUrl = window.location.search;
    else
      yahooUrl = getYahooRSUri({
        rootDomain: landerConfig.domain.rootDomain,
        system: landerConfig.system,
        customerId: landerConfig.customerId,
        typeTag: landerConfig.yahoo?.typeTag,
      });

    yahooXmlFetch(yahooUrl).then((v) => {
      if (ignore) return;
      setFetchState(v.fetchStatus);
      setXmlResult(v.xmlResult);

      if (v.fetchStatus === FetchStatus.FAILURE) {
        sendYahooEvent(EventType.ERROR, landerConfig, {
          yahooApiMessage: v.message,
          errorCode: isAdClicked
            ? ErrorCode.ERROR_YAHOO_FAILED_FETCH_ADS
            : ErrorCode.ERROR_YAHOO_FAILED_FETCH_RS,
        });
      }
    });

    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    landerConfig.domain.rootDomain,
    landerConfig.system,
    landerConfig.customerId,
    landerConfig.yahoo?.typeTag,
    isAdClicked,
  ]);

  let yahooContent: React.ReactNode;
  if (fetchState === FetchStatus.PENDING) {
    yahooContent = null;
  } else if (fetchState === FetchStatus.SUCCESS) {
    yahooContent = (
      <YahooAdContent
        isAdClicked={ isAdClicked }
        xmlResult={ xmlResult }
        keywords={ mergedConfig.keywords }
        maxAds={ maxAds }
        maxRS={ maxRS }
        adIcon={ adIcon }
        headerMessageId={ headerMessageId }
      />
    );
  } else if (
    fetchState === FetchStatus.FAILURE ||
    fetchState === FetchStatus.BLOCKED
  ) {
    yahooContent = (
      <div className={ classes.blockedContent }>
        <FormattedMessage id="contentBlockedText" />
      </div>
    );
  }

  return (
    <div
      className={ classNames(classes.yahooFeed, {
        [classes.pwYahoo]: landerConfig.system === System.PARKWEB,
      }) }
    >
      {yahooContent}
    </div>
  );
};
